import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import salaryService from '../../services/SalaryService.js';
import { Wallet, Calendar, HandCoins } from 'lucide-react';
import { FormLayout, FormField, Button } from '../common/index.js';

const AddSalary = () => {
  const [formData, setFormData] = useState({
    user_id: '',
    month: '',
    year: new Date().getFullYear().toString(),
    basic_salary: '',
    allowances: '0',
    deductions: '0',
    bonus: '0',
    overtime_pay: '0',
    payment_mode: '',
    transaction_id: '',
    remarks: '',
    status: 'pending'
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [netSalary, setNetSalary] = useState(0);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const navigate = useNavigate();

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);
  const statuses = ['pending', 'processing', 'credited', 'hold', 'cancelled'];
  const paymentModes = ['upi', 'cash', 'bank_transfer', 'cheque', 'online'];

  const [loanData, setLoanData] = useState(null);
  const [applyAttendanceDeduction, setApplyAttendanceDeduction] = useState(false);
  const [applyLoanDeduction, setApplyLoanDeduction] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchMonths();
  }, []);

  // Re-fetch users when month or year changes
  useEffect(() => {
    if (formData.month && formData.year) {
      fetchUsers();
    }
  }, [formData.month, formData.year]);

  useEffect(() => {
    calculateNetSalary();
  }, [formData.basic_salary, formData.allowances, formData.deductions, formData.bonus, formData.overtime_pay]);

  useEffect(() => {
    if (attendanceData && loanData) {
      let totalDeductions = parseFloat(formData.deductions) || 0;
      
      // Reset to base deductions
      totalDeductions = 0;
      
      // Add attendance deduction if checked
      if (applyAttendanceDeduction) {
        totalDeductions += attendanceData.salary_calculation.attendance_deduction;
      }
      
      // Add loan deduction if checked
      if (applyLoanDeduction && loanData.total_monthly_installment > 0) {
        totalDeductions += loanData.total_monthly_installment;
      }
      
      setFormData(prev => ({
        ...prev,
        deductions: totalDeductions.toFixed(2)
      }));
    }
  }, [applyAttendanceDeduction, applyLoanDeduction, attendanceData, loanData]);

  const fetchUsers = async () => {
    try {
      // Fetch available users based on selected month and year
      const response = await salaryService.getAvailableUsers(
        formData.month || null,
        formData.year || null
      );
      setUsers(response.data || []);
    } catch (err) {
      Alert.error('Failed to load users');
      console.error(err);
    }
  };

  const fetchMonths = async () => {
    try {
      const response = await salaryService.getAvailableMonths();
      setMonths(response.data || []);
    } catch (err) {
      console.error('Failed to load months', err);
    }
  };

  const calculateNetSalary = () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const overtime = parseFloat(formData.overtime_pay) || 0;
    
    const net = basic + allowances + bonus + overtime - deductions;
    setNetSalary(net);
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setFormData((prev) => ({ ...prev, user_id: userId }));
    
    // Auto-fill monthly salary when user is selected
    if (userId) {
      const selectedUser = users.find(u => u.user_id === userId);
      if (selectedUser && selectedUser.monthly_salary) {
        setFormData((prev) => ({ 
          ...prev, 
          basic_salary: selectedUser.monthly_salary.toString() 
        }));
      }
    }
    
    // Reset attendance data when user changes
    setAttendanceData(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Reset attendance data when month or year changes
    if (name === 'month' || name === 'year') {
      setAttendanceData(null);
    }
  };

  const fetchAttendanceCalculation = async () => {
    if (!formData.user_id || !formData.month || !formData.year) {
      Alert.error('Please select User, Month, and Year first');
      return;
    }

    setLoadingAttendance(true);
    try {
      const response = await salaryService.calculateWithDeductions(
        formData.user_id,
        formData.month,
        formData.year
      );
      
      setAttendanceData(response.data);
      setLoanData(response.data.loan_information);
      
      Alert.success('Salary calculated with loan information!');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to calculate salary');
      setAttendanceData(null);
      setLoanData(null);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Payment screenshot must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    if (!formData.user_id) {
      Alert.error('Please select a user');
      return false;
    }
    if (!formData.month) {
      Alert.error('Please select a month');
      return false;
    }
    if (!formData.year) {
      Alert.error('Please select a year');
      return false;
    }
    if (!formData.basic_salary || parseFloat(formData.basic_salary) <= 0) {
      Alert.error('Please enter a valid basic salary');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        apply_attendance_deduction: applyAttendanceDeduction,
        apply_loan_deduction: applyLoanDeduction,
        loan_deduction_amount: applyLoanDeduction && loanData ? loanData.total_monthly_installment : 0
      };
      
      // Use the appropriate service method based on whether loan deduction is applied
      if (applyLoanDeduction && loanData && loanData.total_monthly_installment > 0) {
        await salaryService.createWithDeductions(submissionData, paymentScreenshot);
      } else {
        await salaryService.create(submissionData, paymentScreenshot);
      }
      
      Alert.success('Salary record created successfully!');
      setTimeout(() => navigate('/salaries'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create salary record');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹ ${Math.round(amount || 0)}`;
  };

  return (
    <FormLayout
      title="Add Salary Record"
      icon={Wallet}
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Creating...' : 'Create Salary Record'}
      loading={loading}
      onCancel={() => navigate('/salaries')}
      maxWidth="max-w-7xl"
    >
      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="User" required>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleUserChange}
              className="input-clean"
              required
            >
                          <option value="">Select User</option>
                          {users.map((user) => (
                            <option key={user.user_id} value={user.user_id}>
                              {user.display_name}
                            </option>
                          ))}
            </select>
          </FormField>

          <FormField label="Month" required>
            <select
              name="month"
              value={formData.month}
              onChange={handleChange}
              className="input-clean"
              required
            >
                          <option value="">Select Month</option>
                          {months.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
            </select>
          </FormField>

          <FormField label="Year" required>
            <select
              name="year"
              value={formData.year}
              onChange={handleChange}
              className="input-clean"
              required
            >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
            </select>
          </FormField>

          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-clean"
            >
                          {statuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
            </select>
          </FormField>

          <div className="sm:col-span-4">
            <Button
              type="button"
              icon={Calendar}
              onClick={fetchAttendanceCalculation}
              disabled={loadingAttendance || !formData.user_id || !formData.month || !formData.year}
            >
              {loadingAttendance ? 'Calculating...' : 'Calculate Salary from Attendance'}
            </Button>
          </div>
        </div>
      </div>

                  {/* Attendance Summary Section */}
                  {attendanceData && (
                    <div className="bg-brand-light border border-blue-200 rounded-sm p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-blue-900 flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Attendance Report - {attendanceData.period.month_display} {attendanceData.period.year}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowAttendanceDetails(!showAttendanceDetails)}
                          className="text-xs text-brand hover:text-blue-800 font-medium"
                        >
                          {showAttendanceDetails ? 'Hide Details' : 'Show Details'}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-3">
                        <div className="bg-white p-3 rounded border border-blue-100">
                          <p className="text-xs text-gray-600 mb-1">Total Days</p>
                          <p className="text-lg sm:text-xl font-bold text-gray-800">{attendanceData.period.total_days}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <p className="text-xs text-gray-600 mb-1">Days Worked</p>
                          <p className="text-lg sm:text-xl font-bold text-green-600">{attendanceData.attendance_summary.days_worked}</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded border border-red-200">
                          <p className="text-xs text-gray-600 mb-1">Days Absent</p>
                          <p className="text-lg sm:text-xl font-bold text-red-600">{attendanceData.attendance_summary.days_absent}</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded border border-purple-200">
                          <p className="text-xs text-gray-600 mb-1">Work Hours</p>
                          <p className="text-sm sm:text-base font-bold text-purple-600">{attendanceData.attendance_summary.total_work_formatted}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3 rounded border border-blue-100">
                        <div>
                          <p className="text-xs text-gray-600">Monthly Salary</p>
                          <p className="text-sm font-semibold text-gray-800">{formatCurrency(attendanceData.salary_calculation.monthly_salary)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Per Day Rate</p>
                          <p className="text-sm font-semibold text-gray-800">{formatCurrency(attendanceData.salary_calculation.per_day_salary)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Calculated Pay</p>
                          <p className="text-sm font-semibold text-green-600">{formatCurrency(attendanceData.salary_calculation.actual_pay_for_days_worked)}</p>
                        </div>
                      </div>

                      {/* Update the existing Deduction Information section */}
                      <div className="bg-red-50 p-3 rounded border border-red-200 mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Deduction for Absent Days</p>
                            <p className="text-lg font-bold text-red-600">{formatCurrency(attendanceData.salary_calculation.attendance_deduction)}</p>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={applyAttendanceDeduction}
                              onChange={(e) => setApplyAttendanceDeduction(e.target.checked)}
                              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="text-xs font-medium text-gray-700">Apply Attendance Deduction</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 italic">
                          Note: Check the box to apply attendance deduction of {formatCurrency(attendanceData.salary_calculation.attendance_deduction)} to the Deductions field.
                        </p>
                      </div>

                      {/* Attendance Details Dropdown */}
                      {showAttendanceDetails && attendanceData.attendance_details.length > 0 && (
                        <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded border border-blue-100">
                          <table className="w-full text-xs">
                            <thead className="bg-blue-100 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 text-left">Date</th>
                                <th className="px-2 py-2 text-left">Login</th>
                                <th className="px-2 py-2 text-left">Logout</th>
                                <th className="px-2 py-2 text-left">Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attendanceData.attendance_details.map((att, idx) => (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-2 py-2">{att.date}</td>
                                  <td className="px-2 py-2">{att.login_time || '-'}</td>
                                  <td className="px-2 py-2">{att.logout_time || '-'}</td>
                                  <td className="px-2 py-2">{att.work_hours}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loan Information Section */}
                  {loanData && loanData.has_active_loans && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-blue-900 flex items-center gap-2">
                          <HandCoins className="w-5 h-5" />
                          Active Loan Information
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
                        <div className="bg-white p-3 rounded border border-blue-100">
                          <p className="text-xs text-gray-600 mb-1">Total Active Loans</p>
                          <p className="text-lg sm:text-xl font-bold text-blue-600">{loanData.total_loans}</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-100">
                          <p className="text-xs text-gray-600 mb-1">Monthly Installment</p>
                          <p className="text-lg sm:text-xl font-bold text-blue-600">{formatCurrency(loanData.total_monthly_installment)}</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-blue-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={applyLoanDeduction}
                              onChange={(e) => setApplyLoanDeduction(e.target.checked)}
                              className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-brand"
                            />
                            <span className="text-xs font-medium text-gray-700">Apply Loan Deduction</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Deduct from salary</p>
                        </div>
                      </div>

                      {/* Loan Details Table */}
                      <div className="bg-white rounded border border-blue-100 overflow-hidden">
                        <div className="overflow-x-auto max-h-48">
                          <table className="w-full text-xs">
                            <thead className="bg-blue-100 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 text-left">Loan ID</th>
                                <th className="px-2 py-2 text-left">Amount</th>
                                <th className="px-2 py-2 text-left">Paid</th>
                                <th className="px-2 py-2 text-left">Pending</th>
                                <th className="px-2 py-2 text-left">Frequency</th>
                                <th className="px-2 py-2 text-left">Monthly Deduction</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loanData.loans.map((loan, idx) => (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-2 py-2 font-medium">{loan.loan_id}</td>
                                  <td className="px-2 py-2">{formatCurrency(loan.loan_amount)}</td>
                                  <td className="px-2 py-2 text-green-600">{formatCurrency(loan.amount_paid)}</td>
                                  <td className="px-2 py-2 text-red-600">{formatCurrency(loan.amount_pending)}</td>
                                  <td className="px-2 py-2 capitalize">{loan.repayment_frequency.replace(/_/g, ' ')}</td>
                                  <td className="px-2 py-2 font-semibold text-blue-600">{formatCurrency(loan.monthly_installment)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <p className="text-xs text-blue-700 mt-3 italic">
                        Note: If you check "Apply Loan Deduction", ₹ {loanData.total_monthly_installment.toFixed(2)} will be automatically deducted and loan repayment records will be created.
                      </p>
                    </div>
                  )}

                  {/* Section 2: Salary Components */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Salary Components
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Basic Salary <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="basic_salary"
                          value={formData.basic_salary}
                          onChange={handleChange}
                          placeholder="Enter basic salary"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Allowances
                        </label>
                        <input
                          type="text"
                          name="allowances"
                          value={formData.allowances}
                          onChange={handleChange}
                          placeholder="Enter allowances"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Deductions
                        </label>
                        <input
                          type="text"
                          name="deductions"
                          value={formData.deductions}
                          onChange={handleChange}
                          placeholder="Enter deductions"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bonus
                        </label>
                        <input
                          type="text"
                          name="bonus"
                          value={formData.bonus}
                          onChange={handleChange}
                          placeholder="Enter bonus"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Overtime Pay
                        </label>
                        <input
                          type="text"
                          name="overtime_pay"
                          value={formData.overtime_pay}
                          onChange={handleChange}
                          placeholder="Enter overtime pay"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Net Salary (Calculated)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(netSalary)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details (Optional)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Mode
                        </label>
                        <select
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        >
                          <option value="">Select Payment Mode</option>
                          {paymentModes.map((mode) => (
                            <option key={mode} value={mode}>
                              {mode.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Screenshot
                        </label>
                        <input
                          id="screenshot-input"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleScreenshotChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload payment screenshot (Max 5MB)</p>
                        {screenshotPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Screenshot Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove screenshot"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Remarks
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes or remarks"
                        rows="4"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                      />
                    </div>
                  </div>
    </FormLayout>
  );
};

export default AddSalary;